import { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardWrapper from 'components/wrappers/DashboardWrapper';
import ToastAlert from 'components/ToastAlert';
import TableContainer from 'components/TableContainer';
import RoleFormModal from './RoleFormModal';
import RoleService from 'api/services/RoleService';
import PropTypes from 'prop-types';

function RoleNameCell({ value }) {
    return <span className="font-medium text-gray-800 text-sm">{value}</span>;
}

function RolePermissionsCell({ value }) {
    const { t } = useTranslation();
    return (
        <div className="flex flex-wrap gap-1">
            {value.length === 0
                ? <span className="px-2 py-0.5 bg-red-50 text-red-500 rounded-full text-xs italic">{t('roles.no_access')}</span>
                : value.map(p => (
                    <span key={p} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs">
                        {t(`roles.permission_descriptions.${p.replace(/[:.]/g, '_')}`)}
                    </span>
                ))
            }
        </div>
    );
}

function RoleProtectedCell({ value }) {
    const { t } = useTranslation();
    return value
        ? <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">{t('roles.protected')}</span>
        : null;
}

function RoleActionsCell({ row, column }) {
    const { t } = useTranslation();
    const role = row.original;
    const isBlocked = role.name === 'Blocked';
    return (
        <div className="flex justify-end gap-2">
            <button
                onClick={() => !isBlocked && column.setEditTarget(role)}
                disabled={isBlocked}
                title={isBlocked ? t('roles.blocked_unmodifiable') : ''}
                className="text-xs px-3 py-1 border rounded text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
            >
                {t('actions.edit')}
            </button>
            <button
                onClick={() => !role.isProtected && column.setDeleteTarget(role.id)}
                disabled={role.isProtected}
                title={role.isProtected ? t('roles.cannot_delete_protected') : ''}
                className="text-xs px-3 py-1 border rounded text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
            >
                {t('actions.delete')}
            </button>
        </div>
    );
}

function Roles() {
    const { t } = useTranslation();
    const [roles, setRoles] = useState([]);
    const [toast, setToast] = useState(null);

    const showSuccess = () => setToast({ type: 'success', message: t('alert.success_operation'), key: Date.now() });
    const showError = (err) => setToast({ type: 'error', message: err?.response?.data?.message || t('alert.failed_operation'), key: Date.now() });

    const [createOpen, setCreateOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const fetchRoles = useCallback(() => {
        RoleService.getRoles()
            .then(data => setRoles(data.results ?? []))
            .catch((err) => setToast({ type: 'error', message: err?.response?.data?.message || t('alert.failed_operation'), key: Date.now() }));
    }, [t]);

    useEffect(() => { fetchRoles(); }, [fetchRoles]);

    const handleCreate = async (data) => {
        try {
            await RoleService.createRole(data);
            showSuccess();
            fetchRoles();
        } catch (err) {
            showError(err);
        }
    };

    const handleUpdate = async (data) => {
        try {
            await RoleService.updateRole(editTarget.id, data);
            showSuccess();
            fetchRoles();
        } catch (err) {
            showError(err);
        }
    };

    const handleDelete = async (id) => {
        try {
            await RoleService.deleteRole(id);
            showSuccess();
            setDeleteTarget(null);
            fetchRoles();
        } catch (err) {
            showError(err);
            setDeleteTarget(null);
        }
    };

    const columns = useMemo(() => [
        {
            Header: t('roles.role_name'),
            accessor: 'name',
            Cell: RoleNameCell,
        },
        {
            Header: t('roles.permissions'),
            accessor: 'permissions',
            disableSortBy: true,
            Cell: RolePermissionsCell,
        },
        {
            Header: t('roles.protected'),
            accessor: 'isProtected',
            disableSortBy: true,
            Cell: RoleProtectedCell,
        },
        {
            Header: '',
            accessor: 'id',
            disableSortBy: true,
            Cell: RoleActionsCell,
            setEditTarget,
            setDeleteTarget,
        },
    ], [t, setEditTarget, setDeleteTarget]);

    return (
        <DashboardWrapper>
            <div className="p-6">

                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">{t('roles.title')}</h2>
                        <p className="text-sm text-gray-500 mt-1">{t('roles.description')}</p>
                    </div>
                    <button
                        onClick={() => setCreateOpen(true)}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                        + {t('roles.create_role')}
                    </button>
                </div>

                <TableContainer
                    data={Array.isArray(roles) ? roles : []}
                    columns={columns}
                    globalSearch={true}
                    isColumnFiltrable={false}
                />

                {/* Delete confirmation */}
                {deleteTarget && (
                    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
                        <div className="bg-white rounded shadow-lg p-6 max-w-sm w-full">
                            <p className="text-gray-800 font-medium mb-4">{t('roles.delete_confirm')}</p>
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => setDeleteTarget(null)}
                                    className="px-4 py-2 text-sm border rounded text-gray-600 hover:bg-gray-50"
                                >
                                    {t('actions.cancel')}
                                </button>
                                <button
                                    onClick={() => handleDelete(deleteTarget)}
                                    className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                                >
                                    {t('actions.delete')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <RoleFormModal
                    open={createOpen}
                    onClose={() => setCreateOpen(false)}
                    onSubmit={handleCreate}
                    role={null}
                />
                <RoleFormModal
                    open={!!editTarget}
                    onClose={() => setEditTarget(null)}
                    onSubmit={handleUpdate}
                    role={editTarget}
                />

                {toast && <ToastAlert key={toast.key} message={toast.message} type={toast.type} timeOut={4000} onClose={() => setToast(null)} />}
            </div>
        </DashboardWrapper>
    );
}

export default Roles;

RoleNameCell.propTypes = {
  value: PropTypes.string,
};

RolePermissionsCell.propTypes = {
  value: PropTypes.array.isRequired,
};

RoleProtectedCell.propTypes = {
  value: PropTypes.bool,
};

RoleActionsCell.propTypes = {
  row: PropTypes.object.isRequired,
  column: PropTypes.object.isRequired,
};
